import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import {
  EXPECTED_ZONE_COUNT, IS_MULTI_ZONE, ONE_ZONE_REASON, ZONES, acknowledgeZoneSession,
  bindZoneSession, consoleZoneLabel, getPreferredZone, resolveZonePreselection, setWorkingZone,
  useWorkingZone, useWorkingZoneCode, useZoneSwitchEpoch, zoneSessionAcknowledged,
  type PortalZone,
} from '../lib/zones';
import { ActionConfirmModal } from '../routes/admin/ActionConfirmModal';
import { useZoneWriteReport } from './ZoneContext';
import './zone-selector.css';

/**
 * ADR-122 Pattern 3 — "explicit zone selector, no merge".
 *
 * ⭐⭐ THIS IS THE PATTERN FOR PAGES WHERE MERGING TWO ZONES WOULD MISREPRESENT STATE RATHER
 * THAN CLARIFY IT. Patterns 1/4/5 fan out and merge because a longer list is a truer list.
 * Here the opposite holds, for two distinct reasons:
 *
 *   - **The page WRITES.** A merged view of two mutation targets is meaningless — an operator
 *     editing "the" availability gate has to know which jurisdiction's gate is about to flip.
 *     Before this component, `NexaConfigPage` silently targeted the operator's home zone with
 *     no indication the other zone existed, so the two zones could hold different gate states
 *     with the console confidently showing one of them. That is the specific risk ADR-122
 *     names as the highest-consequence finding in its whole inventory.
 *   - **The stores are genuinely separate systems.** `FlowValidatorPage` and
 *     `SnippetTelemetryPage` read per-zone Tinybird workspaces, which ADR-120 Stage 4 proved
 *     are separate, matched-region stores with no shared key. There is no merge of two
 *     different analytics backends that means anything.
 *
 * ── Design constraints, from ADR-122 Part A Pattern 3 ──────────────────────────────────────
 *
 * 1. **Always visible in the page chrome** — a segmented control, NOT a dropdown that can be
 *    missed. Which zone an edit targets must never require a click to discover.
 * 2. **Defaults per ADR-123 Decision 3** (`resolveZonePreselection`): an explicit `?zone=` in
 *    the URL, else this page's own selection earlier in this browser session, else the staff
 *    user's preferred zone, else the platform home zone. Never another staff member's choice,
 *    and never a selection made on a DIFFERENT page — which is how an operator ends up editing
 *    a zone they did not mean to, having never looked at the control. Whatever wins is stated in
 *    the navigation bar ("Editing us-east-1 · United States").
 * 3. **Built once and reused**, rather than as three independent implementations
 *    (ADR-122 Consequences; failure mode #11 — "why new components keep appearing").
 *
 * ⚠ ADR-123 Decision 2 — IT ALWAYS RENDERS. It used to render nothing with one zone
 * configured; that made a deploy missing the US address look like a normal AU console. With one
 * zone it now renders that zone, disabled, with "only one zone configured" beside it.
 */

/**
 * "This page's own selection this session" (precedence level 2). Held in memory for the life of
 * the tab's single-page app, keyed by page — so leaving Nexa Config and coming back keeps the
 * zone the operator chose there, while a reload starts again from the URL / preference / home.
 * Deliberately NOT sessionStorage or localStorage: nothing about another page, another tab or a
 * previous visit may decide which zone a write lands in.
 */
const pageSelections = new Map<string, string>();

/** Test seam — the selections are module state, so a test file must be able to start clean. */
export function resetZoneSelectionsForTests(): void {
  pageSelections.clear();
}

/**
 * Owns the selected-zone state for a Pattern 3 page.
 *
 * Returns the resolved `PortalZone` rather than a bare code, because that is what
 * `adminApiInZone` takes — handing back a string would make every caller re-resolve it, and a
 * caller that forgot would silently fall back to the home zone, which is the exact defect this
 * pattern exists to remove.
 */
export function useZoneSelection(
  /** Which page this selection belongs to, e.g. "configuration" — the precedence's level 2 key. */
  scope: string,
  {
    /**
     * False while the page is showing something the selector does NOT govern (Nexa Config's
     * platform-wide gate tab), so the navigation bar is not told a zone-scoped write is in play.
     */
    reportWrite = true,
  }: { reportWrite?: boolean } = {},
): {
  zone: PortalZone;
  zoneCode: string;
  setZoneCode: (code: string) => void;
} {
  const [searchParams, setSearchParams] = useSearchParams();
  // ADR-124 A2 — level 3 is the session's working zone (the preferred zone until switched).
  const preferred = useWorkingZoneCode();
  const [selection, setSelection] = useState<string | null>(() => pageSelections.get(scope) ?? null);
  // A confirmed console-wide switch told the operator that EVERYTHING now targets the new zone, so
  // a page selection made before it must not survive it — including on a page already mounted.
  // The URL `?zone=` is moved too, since it would otherwise outrank the switch.
  const epoch = useZoneSwitchEpoch();
  const seenEpoch = useRef(epoch);
  useEffect(() => {
    if (seenEpoch.current === epoch) return;
    seenEpoch.current = epoch;
    setSelection(null);
    if (searchParams.has('zone')) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('zone');
        return next;
      }, { replace: true });
    }
  }, [epoch, searchParams, setSearchParams]);
  const zone = resolveZonePreselection({
    urlZone: searchParams.get('zone'),
    pageSelection: selection,
    preferred,
    zones: ZONES,
  });
  // The navigation bar names the one zone this page's writes land in.
  useZoneWriteReport(reportWrite ? zone.code : null);
  const setZoneCode = (code: string) => {
    pageSelections.set(scope, code);
    setSelection(code);
    // An explicit `?zone=` outranks the page selection, so a switch made while one is present
    // must move the URL too — otherwise the link would silently win and the click do nothing.
    if (searchParams.has('zone')) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('zone', code);
        return next;
      }, { replace: true });
    }
  };
  return { zone, zoneCode: zone.code, setZoneCode };
}

export interface ZoneSelectorProps {
  /** The currently-selected zone code — `zoneCode` from `useZoneSelection`. */
  value: string;
  onChange: (code: string) => void;
  /**
   * What the operator is choosing the zone FOR, e.g. "configuration" — completes the accessible
   * label ("Zone for configuration"). Keep it a noun phrase, lowercase.
   */
  scopeLabel: string;
  /** Disabled while a read or write is in flight, so a mid-request switch cannot race. */
  disabled?: boolean;
}

export function ZoneSelector({ value, onChange, scopeLabel, disabled }: ZoneSelectorProps) {
  // ADR-124 A2 — a click does not switch; it ASKS. The switch lands only on Confirm.
  const [pending, setPending] = useState<string | null>(null);
  const working = useWorkingZone();
  return (
    <div className="zone-selector" role="group" aria-label={`Zone for ${scopeLabel}`}>
      <ZoneSwitchConfirm
        open={pending !== null}
        target={pending ?? value}
        pageScope={scopeLabel}
        workingZone={working.code}
        onConfirm={(code) => { setPending(null); onChange(code); }}
        onCancel={() => setPending(null)}
      />
      <span className="zone-selector-label">Zone</span>
      <div className="zone-selector-options">
        {ZONES.map((z) => {
          const selected = z.code === value;
          return (
            <button
              key={z.code}
              type="button"
              className={`zone-selector-option${selected ? ' selected' : ''}`}
              // `aria-pressed` rather than `aria-current`: this is a toggle group over a
              // mutually-exclusive set, not navigation between pages.
              aria-pressed={selected}
              disabled={disabled || !IS_MULTI_ZONE}
              title={IS_MULTI_ZONE ? undefined : ONE_ZONE_REASON}
              onClick={() => { if (z.code !== value) setPending(z.code); }}
            >
              {z.label}
              {z.home && <span className="zone-selector-home"> · home</span>}
            </button>
          );
        })}
      </div>
      {!IS_MULTI_ZONE && <span className="zone-disabled-reason">{ONE_ZONE_REASON}</span>}
    </div>
  );
}

/**
 * The standing "you are looking at ONE zone" line that sits under a Pattern 3 page's selector.
 *
 * ⭐ A SELECTOR ALONE DOES NOT CLOSE THE RISK. An operator who never touches the control still
 * has to understand that what is on screen is one jurisdiction's state and not the platform's.
 * The selector answers "which zone?" only for someone who already knows to ask; this sentence
 * is what tells them there is a question. Always rendered (ADR-123 Decision 2) — with one
 * zone configured it is still true that only that zone is shown.
 */
export function ZoneScopeNote({ zone, noun }: { zone: PortalZone; noun: string }) {
  return (
    <p className="zone-selector-note">
      Showing <strong>{zone.label}</strong> only. {noun} in other zones are held separately and
      are not shown or changed here.
    </p>
  );
}

// ── ADR-124 A2/A3 — the zone switch is a confirmed act; the zone is stated at every sign-in ─────

/**
 * The switch confirmation (A2). Composed from `ActionConfirmModal` — the console's one
 * confirmation dialog — never a modal of its own (Navigation Bar Reference §11.6, "why new
 * components keep appearing").
 *
 * Two scopes, stated differently because they ARE different:
 *   - console-wide (the navigation-bar "Switch zone…", or "Switch zone…" from the sign-in modal):
 *     the session's working zone moves, and every page follows;
 *   - one page (`pageScope` — a Pattern 3 switcher on Nexa Config, Flow Validator, Snippet
 *     Telemetry): that page's target moves and other pages keep the working zone. ADR-122's
 *     Pattern 3 constraint — never default a write from a selection made on a DIFFERENT page —
 *     still holds, so the sentence says so rather than claiming more than the switch does.
 *
 * With `choose`, the operator picks the zone inside the dialog (the console-wide entry points,
 * which have no zone buttons of their own); the sentence always names the zone being entered.
 */
export function ZoneSwitchConfirm({
  open,
  target,
  pageScope,
  workingZone,
  choose = false,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  /** The zone being entered (with `choose`, the initial pick). */
  target: string;
  /** Set for a one-page switch — the page's scope noun, e.g. "configuration". */
  pageScope?: string;
  /** The session's working zone code — named when a page switch leaves it unchanged. */
  workingZone: string;
  choose?: boolean;
  onConfirm: (code: string) => void;
  onCancel: () => void;
}) {
  const [picked, setPicked] = useState(target);
  useEffect(() => { if (open) setPicked(target); }, [open, target]);
  const code = choose ? picked : target;
  if (!open) return null;
  // ⚠ PORTALLED TO <body>: the navigation-bar entry point sits inside the sticky, overflow-hidden
  // zone row, whose stacking context would otherwise trap the dialog beneath the page.
  return createPortal(
    <ActionConfirmModal
      open={open}
      title={pageScope ? `Switch zone for ${pageScope}?` : 'Switch working zone?'}
      intent="warning"
      primaryLabel="Confirm"
      onConfirm={() => onConfirm(code)}
      onCancel={onCancel}
      hideClose
    >
      {choose && (
        <fieldset className="zone-switch-choices">
          <legend>Zone</legend>
          {ZONES.map((z) => (
            <label key={z.code} className="zone-switch-choice">
              <input
                type="radio"
                name="zone-switch-choice"
                value={z.code}
                checked={picked === z.code}
                onChange={() => setPicked(z.code)}
              />
              {z.label}
              {z.code === workingZone && <span className="zone-selector-home"> · current</span>}
            </label>
          ))}
        </fieldset>
      )}
      <p data-testid="zone-switch-sentence">
        You are switching to <strong>{consoleZoneLabel(code)}</strong>.{' '}
        {pageScope
          ? <>Everything you view or edit on this page targets this zone until you switch back. Other pages stay in your working zone, <strong>{consoleZoneLabel(workingZone)}</strong>.</>
          : 'Everything you view, edit or create from here targets this zone until you switch back.'}
      </p>
    </ActionConfirmModal>,
    document.body,
  );
}

/**
 * The navigation-bar working-zone control (A2): the session's zone, and the one console-wide way
 * to change it. Always rendered (ADR-123 Decision 2); with one zone configured it is present and
 * disabled, with the reason on screen.
 */
export function WorkingZoneSwitcher({
  openRequest = 0,
}: {
  /** Bumped by the sign-in modal's "Switch zone…" to open the switch dialog from outside. */
  openRequest?: number;
}) {
  const working = useWorkingZone();
  const [open, setOpen] = useState(false);
  const firstOther = ZONES.find((z) => z.code !== working.code)?.code ?? working.code;
  useEffect(() => { if (openRequest > 0 && IS_MULTI_ZONE) setOpen(true); }, [openRequest]);
  return (
    <div className="zone-working" data-testid="working-zone">
      <span className="zone-working-label">Working in</span>
      <span className="zone-indicator-seg">{working.label}</span>
      <button
        type="button"
        className="zone-working-switch"
        disabled={!IS_MULTI_ZONE}
        title={IS_MULTI_ZONE ? undefined : ONE_ZONE_REASON}
        onClick={() => setOpen(true)}
      >
        Switch zone…
      </button>
      {!IS_MULTI_ZONE && <span className="zone-disabled-reason">{ONE_ZONE_REASON}</span>}
      <ZoneSwitchConfirm
        open={open}
        choose
        target={firstOther}
        workingZone={working.code}
        onConfirm={(code) => { setOpen(false); if (code !== working.code) setWorkingZone(code); }}
        onCancel={() => setOpen(false)}
      />
    </div>
  );
}

/**
 * The sign-in statement (A3): before the console is used, the session is told which zone it is
 * starting in. Once per session — a refresh within the session does not re-prompt (the
 * acknowledgement is in sessionStorage, keyed by the staff sub); a new sign-in clears it.
 *
 * `ready` waits for `GET /admin/me`, so the zone stated is the one the session will actually use
 * (the preferred zone arrives with it); stating the home zone and then quietly moving to the
 * preferred zone a moment later is exactly the unannounced switch this exists to prevent.
 */
export function ZoneSessionGate({
  sub,
  ready,
  onSwitch,
}: {
  sub: string | undefined;
  ready: boolean;
  /** "Switch zone…" — opens the A2 switch dialog. */
  onSwitch: () => void;
}) {
  const working = useWorkingZone();
  const [acked, setAcked] = useState(false);
  useEffect(() => { bindZoneSession(sub); setAcked(zoneSessionAcknowledged()); }, [sub]);
  if (!sub || !ready || acked) return null;
  const preferred = getPreferredZone();
  const why = preferred && preferred === working.code
    ? '(your default zone)'
    : preferred
      ? `(your default zone, ${consoleZoneLabel(preferred)}, is not configured in this console)`
      : '(the platform home zone — no default zone is set on your profile)';
  const acknowledge = () => {
    // The zone stated becomes the session's working zone explicitly, so a later change to the
    // preference on My Profile cannot move this session without the switch modal.
    setWorkingZone(working.code);
    acknowledgeZoneSession();
    setAcked(true);
  };
  return (
    <ActionConfirmModal
      open
      title="Your working zone"
      intent="primary"
      // The acknowledgement IS the main action — the console's standard primary button, not the
      // bare button style `intent="primary"` falls to (seen in the first live capture).
      primaryClassName="portal-btn"
      primaryLabel="Continue in this zone"
      onConfirm={acknowledge}
      onCancel={() => { /* a statement to acknowledge, not a choice to dismiss */ }}
      hideClose
      hideCancel
      extraAction={
        <button
          type="button"
          className="portal-link-button-secondary"
          disabled={!IS_MULTI_ZONE}
          title={IS_MULTI_ZONE ? undefined : ONE_ZONE_REASON}
          onClick={() => { acknowledge(); onSwitch(); }}
        >
          Switch zone…
        </button>
      }
    >
      <p data-testid="zone-session-sentence">
        You are working in <strong>{working.label}</strong> {why}.
      </p>
      <p>Everything you view, edit or create targets this zone until you switch.</p>
      {ZONES.length < EXPECTED_ZONE_COUNT && (
        <p className="zone-indicator-warn" data-testid="zone-session-misconfigured">
          {ZONES.length} {ZONES.length === 1 ? 'zone' : 'zones'} configured — expected {EXPECTED_ZONE_COUNT}.
          Switching is unavailable: {ONE_ZONE_REASON}.
        </p>
      )}
    </ActionConfirmModal>
  );
}
